import { useRef } from "react";
import { toast } from "sonner";

import { useCreateClientMutation } from "Clients/Api/Mutations/useCreateClientMutation";
import { ClientForm, type ClientFormHandle } from "Clients/Forms/ClientForm";
import {
  type ClientFormData,
  toSaveClientRequest,
} from "Clients/Forms/clientFormSchema";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { BlModal } from "Shared/Dialogs/BlModal";

interface CreateClientModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CreateClientModal = ({
  isOpen,
  onClose,
}: CreateClientModalProps): React.ReactNode => {
  const { t } = useResource();
  const formRef = useRef<ClientFormHandle | null>(null);
  const createClientMutation = useCreateClientMutation();

  const onSubmit = async (formData: ClientFormData) => {
    try {
      await createClientMutation.mutateAsync(toSaveClientRequest(formData));
      toast.success(t(Resources.Clients.CreateClientModal.SuccessMessage));
      formRef.current?.reset();
      onClose();
    } catch {
      // field errors shown in form, non-field errors shown as toast via useHandleServerError
    }
  };

  const handleClose = () => {
    formRef.current?.reset();
    onClose();
  };

  return (
    <BlModal
      isOpen={isOpen}
      title={t(Resources.Clients.CreateClientModal.Title)}
      onCancel={handleClose}
      onConfirm={() => formRef.current?.submit(onSubmit)()}
      cancelButtonText={t(Resources.Common.Cancel)}
      confirmButtonText={t(Resources.Clients.CreateClientModal.Create)}
      maxWidth="800px"
    >
      <ClientForm
        client={null}
        formRef={formRef}
        serverError={createClientMutation.error}
      />
    </BlModal>
  );
};
